import React, { Component } from "react";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { updateProfile } from "../../actions/authActions";
import axios from "axios";
import { apiUrl } from "../../config/config";

import SideBar from "../../components/common/SideBar";
import AttendanceView from "../Verifier-dashboard/components/AttendanceView";
import { performLogout } from "../../utils/logout";
import "./VerifierProfile.css";

// Public hostname for each server's Open file hand-off redirect — same
// "<name>.mybiocalculus.com" pattern web1 already uses (see Login.js's
// isWeb1 check). Not from config/servers.js: that file holds internal
// API base URLs and only exists on deployed servers, not this frontend
// build, and isn't the same thing as the public-facing hostname anyway.
const SERVER_HOSTS = {
  web: "web.mybiocalculus.com",
  web1: "web1.mybiocalculus.com",
  web2: "web2.mybiocalculus.com",
  web4: "web4.mybiocalculus.com",
  web5: "web5.mybiocalculus.com",
  web6: "web6.mybiocalculus.com",
};

// "28m" / "1h 05m" from an assignedAt timestamp.
function elapsedSince(dateStr) {
  if (!dateStr) return "—";
  const ms = Date.now() - new Date(dateStr).getTime();
  if (!(ms > 0)) return "0m";
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m`;
}

// Inline-style "Log Out" button shared by every profile page so the
// option is always visible and looks identical across roles. Uses the
// same `performLogout` helper SideBar uses, so the close-out behavior
// (verifier-attendance API + activity flush + redirect) stays in sync.
const ProfileLogoutButton = () => (
  <button
    type="button"
    onClick={(e) => {
      if (e && e.preventDefault) e.preventDefault();
      performLogout();
    }}
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "8px 18px",
      borderRadius: 8,
      border: "1px solid rgba(239,68,68,0.35)",
      background: "rgba(239,68,68,0.10)",
      color: "#ff6b6b",
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
      transition: "background 0.15s ease, border-color 0.15s ease",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = "rgba(239,68,68,0.18)";
      e.currentTarget.style.borderColor = "rgba(239,68,68,0.55)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = "rgba(239,68,68,0.10)";
      e.currentTarget.style.borderColor = "rgba(239,68,68,0.35)";
    }}
  >
    <span aria-hidden="true">🚪</span> Log Out
  </button>
);

class VerifierProfile extends Component {
  constructor() {
    super();
    this.state = {
      // Sidebar menu — Patient list / Verifier home / Verified / QC
      menus: (function () {
        var _menus = [
          { title: "Patient List", url: "/patient-list" },
          { title: "Verifier Home", url: "/verifier-home" },
          { title: "Verified", url: "/Verification-Completed" },
          { title: "QC", url: "/qc_home" },
        ];
        // Restricted Verifier tier (type 10) only: same "Queue" link as
        // patient-list.js's sidebar — filters the Patient List table down
        // to just the patients actually assigned to this verifier.
        if (localStorage.getItem("verifiertype") === "10") {
          _menus.splice(1, 0, { title: "Queue", url: "/patient-list?queue=1" });
        }
        return _menus;
      })(),
      // Restricted menu for verifiertype === "7" (kept compatible with other pages)
      menu: [{ title: "Patient List", url: "/Partner" }],
      profileLink: "verifier-profile",
      name: "",
      phone: "",
      email: "",
      gender: "M",
      address: "",
      errors: {},
      saving: false,
      savedAt: null,

      // ── My work: one-block-at-a-time queue (2026-11 spec) ─────────
      myActive:          null,   // the block currently assigned to me, or null
      myReanalysis:      [],     // blocks I set aside, each with a `ready` flag
      myCompletedToday:  [],
      myWorkLoading:     false,
      myWorkError:       "",
      myWorkFlash:       "",     // transient success message
      myWorkBusy:        false,  // a request/complete/reanalysis POST is in flight
      openingFile:        false, // cross-server token mint in flight (Open file)
      openingFileError:   "",
      clockTick:          0,     // bumped every 30s purely to re-render "time taken"

      // ── Today's verified patients (patient id / name / file count) ──
      verifiedToday:        [],
      verifiedTodayLoading: false,
      verifiedTodayError:   "",
    };
  }

  componentDidMount() {
    axios
      .get(apiUrl + "api/users/profile")
      .then((res) => {
        this.setState({
          name: res.data.name || "",
          phone: res.data.phone || "",
          email: res.data.email || "",
          gender: res.data.gender || "M",
          address: res.data.address || "",
          id: res.data._id,
        });
      })
      .catch(() => {});

    this.loadVerifiedToday();
    this.loadMyWork();

    // Re-fetch on tab focus so the list/counts roll over to the new day
    // (and pick up anything verified elsewhere) without needing a manual
    // reload — same pattern AttendanceView.js uses.
    this.onFocusRefresh = () => {
      this.loadVerifiedToday();
      this.loadMyWork();
    };
    window.addEventListener("focus", this.onFocusRefresh);

    // My work polls on two cadences: a cheap 30s tick just re-renders the
    // "time taken" clock on the current block (no network call), and a
    // 60s refetch picks up server-side changes — the 2-hour auto-return,
    // the 15-minute warning, a reanalysis block becoming ready, or (once
    // Mark completed/Request file resolve) the next block.
    this._clockInterval = window.setInterval(() => {
      this.setState((s) => ({ clockTick: s.clockTick + 1 }));
    }, 30000);
    this._myWorkInterval = window.setInterval(() => {
      this.loadMyWork();
    }, 60000);
  }

  componentWillUnmount() {
    if (this.onFocusRefresh) {
      window.removeEventListener("focus", this.onFocusRefresh);
    }
    if (this._clockInterval) window.clearInterval(this._clockInterval);
    if (this._myWorkInterval) window.clearInterval(this._myWorkInterval);
  }

  // Fetch the list of patients THIS verifier verified today, with file
  // counts. Backend route is GET /api/users/verifierAttendance/patients
  // — scoped by verifierId + today's date (IST), same source the
  // attendance dashboard uses.
  loadVerifiedToday = () => {
    var verifierId =
      typeof window !== "undefined" ? localStorage.getItem("verifierid") : "";
    if (!verifierId) {
      this.setState({ verifiedTodayError: "No verifier ID in session." });
      return;
    }
    var pad = function (n) { return String(n).padStart(2, "0"); };
    var now = new Date(Date.now() + (5 * 60 + 30) * 60 * 1000); // IST
    var todayStr =
      now.getUTCFullYear() + "-" + pad(now.getUTCMonth() + 1) + "-" + pad(now.getUTCDate());

    this.setState({ verifiedTodayLoading: true, verifiedTodayError: "" });
    axios
      .get(
        apiUrl + "api/users/verifierAttendance/patients" +
        "?verifierId=" + encodeURIComponent(verifierId) +
        "&date=" + todayStr +
        "&_t=" + Date.now()
      )
      .then((res) => {
        var data = res.data || {};
        var list = Array.isArray(data.patients) ? data.patients : [];
        this.setState({ verifiedToday: list, verifiedTodayLoading: false });
      })
      .catch((err) => {
        this.setState({
          verifiedTodayLoading: false,
          verifiedTodayError:
            (err.response && err.response.data && err.response.data.error)
            || err.message || "Failed to load today's verified patients",
        });
      });
  };

  // ── My work: one-block-at-a-time queue ──────────────────────────────
  // GET /verifierQueue/mine → { active, reanalysis } and
  // GET /verifierQueue/completedToday → { blocks }. Combined into one
  // loader since the UI renders all three together.
  loadMyWork = async () => {
    this.setState({ myWorkLoading: true, myWorkError: "" });
    try {
      const [mineRes, doneRes] = await Promise.all([
        axios.get(apiUrl + "api/users/verifierQueue/mine"),
        axios.get(apiUrl + "api/users/verifierQueue/completedToday"),
      ]);
      const mine = mineRes.data || {};
      const done = doneRes.data || {};
      this.setState({
        myActive: mine.active || null,
        myReanalysis: Array.isArray(mine.reanalysis) ? mine.reanalysis : [],
        myCompletedToday: Array.isArray(done.blocks) ? done.blocks : [],
        myWorkLoading: false,
      });
    } catch (err) {
      this.setState({
        myWorkLoading: false,
        myWorkError:
          (err.response && err.response.data && err.response.data.error)
          || err.message || "Failed to load your work.",
      });
    }
  };

  requestBlock = async () => {
    this.setState({ myWorkBusy: true, myWorkError: "", myWorkFlash: "" });
    try {
      const res = await axios.post(apiUrl + "api/users/verifierQueue/request", {});
      const data = res.data || {};
      if (!data.block) {
        this.setState({
          myWorkBusy: false,
          myWorkError: data.message || "Nothing available in the queue right now.",
        });
        return;
      }
      this.setState({
        myWorkBusy: false,
        myWorkFlash: data.fromReanalysis
          ? "Back on your reanalysis block."
          : data.alreadyHeld
            ? "You already have an active block."
            : "New block assigned.",
      });
      this.loadMyWork();
    } catch (err) {
      this.setState({
        myWorkBusy: false,
        myWorkError:
          (err.response && err.response.data && err.response.data.error)
          || err.message || "Request failed.",
      });
    }
  };

  // Also used for a block still parked for reanalysis — pass it explicitly
  // from the "Waiting for reanalysis" row so it can be marked completed
  // directly, without first re-requesting it back as the active block.
  markCompleted = async (block) => {
    const b = block || this.state.myActive;
    if (!b) return;
    this.setState({ myWorkBusy: true, myWorkError: "", myWorkFlash: "" });
    try {
      await axios.post(apiUrl + "api/users/verifierQueue/complete", {
        server: b.server, fileId: b.fileId, blockIndex: b.blockIndex,
      });
      this.setState({ myWorkBusy: false, myWorkFlash: "Marked completed." });
      this.loadMyWork();
    } catch (err) {
      this.setState({
        myWorkBusy: false,
        myWorkError:
          (err.response && err.response.data && err.response.data.error)
          || err.message || "Could not mark completed.",
      });
    }
  };

  markReanalysis = async () => {
    const b = this.state.myActive;
    if (!b) return;
    this.setState({ myWorkBusy: true, myWorkError: "", myWorkFlash: "" });
    try {
      await axios.post(apiUrl + "api/users/verifierQueue/reanalysis", {
        server: b.server, fileId: b.fileId, blockIndex: b.blockIndex,
      });
      this.setState({ myWorkBusy: false, myWorkFlash: "Set aside for reanalysis." });
      this.loadMyWork();
    } catch (err) {
      this.setState({
        myWorkBusy: false,
        myWorkError:
          (err.response && err.response.data && err.response.data.error)
          || err.message || "Could not set aside for reanalysis.",
      });
    }
  };

  // Opens a block's file. Same server: a normal in-app navigation. A
  // different server: mint a short-lived single-use sign-in token here and
  // send the verifier to that server's /sso landing page, which redeems it
  // and drops them straight on the file — no search, no second login.
  // See SsoRedirect.js + POST /verifierQueue/openFileToken.
  openFile = async (block) => {
    if (!block || !block.lastDatasetId) {
      this.setState({ openingFileError: "This file isn't ready to open yet (no dataset on record)." });
      return;
    }
    const mySever = process.env.REACT_APP_SERVER || "web";
    // Type 10 (restricted Verifier tier) opens straight into the analysed
    // chart view (Afib/Missed Beats/Verify toolbar) instead of the raw
    // unanalysed view other verifier types land on.
    const basePath = localStorage.getItem("verifiertype") === "10" ? "/verifier-chart" : "/verifier-unanalysed";
    if (block.server === mySever) {
      this.props.history.push(`${basePath}/${block.fileId}/${block.lastDatasetId}`);
      return;
    }
    this.setState({ openingFile: true, openingFileError: "" });
    try {
      const res = await axios.post(apiUrl + "api/users/verifierQueue/openFileToken", {});
      const { token, server } = res.data || {};
      const email = encodeURIComponent(localStorage.getItem("verifieremail") || "");
      const host = SERVER_HOSTS[block.server] || `${block.server}.mybiocalculus.com`;
      const url =
        `https://${host}/sso?token=${encodeURIComponent(token)}` +
        `&email=${email}&src=${encodeURIComponent(server)}` +
        `&patientId=${encodeURIComponent(block.fileId)}` +
        `&lastDatasetId=${encodeURIComponent(block.lastDatasetId)}`;
      window.location.href = url;
    } catch (err) {
      this.setState({
        openingFile: false,
        openingFileError:
          (err.response && err.response.data && err.response.data.error)
          || err.message || "Could not open the file on " + block.server + ".",
      });
    }
  };

  componentWillReceiveProps(nextProps) {
    if (nextProps.errors) {
      this.setState({ errors: nextProps.errors });
    }
  }

  onChange = (e) => {
    const err = { ...this.state.errors };
    delete err[e.target.name];
    this.setState({ [e.target.name]: e.target.value, errors: err });
  };

  onSubmit = (e) => {
    e.preventDefault();
    this.setState({ saving: true });
    const user = {
      id: this.state.id,
      name: this.state.name,
      phone: this.state.phone,
      email: this.state.email,
      gender: this.state.gender,
      address: this.state.address,
    };
    this.props.updateProfile(user, this.props.history);
    // Optimistic feedback — the action is fire-and-forget and may navigate.
    setTimeout(() => {
      this.setState({ saving: false, savedAt: new Date() });
    }, 600);
  };

  render() {
    const { errors, name, email, phone, address, gender, saving, savedAt } =
      this.state;

    return (
      <div className="vp-page">
        <SideBar
          menus={
            localStorage.getItem("verifiertype") !== "7"
              ? this.state.menus
              : this.state.menu
          }
        />

        <div className="page-wrapper">
          <div className="vp-container">
            {/* Header — title + Log Out (visible regardless of sidebar
                layout / clipping). */}
            <div
              className="vp-header"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              <div>
                <h2>Profile</h2>
                <p>Attendance, verified activity and personal details</p>
              </div>
              <ProfileLogoutButton />
            </div>

            {/* ── My work (2026-11 spec: one block at a time) ─────── */}
            <div className="myw-header">
              <h2>My work</h2>
              <p>
                {this.state.myCompletedToday.length} block
                {this.state.myCompletedToday.length === 1 ? "" : "s"} completed today
                {" · "}
                {this.state.myReanalysis.length} waiting for reanalysis
              </p>
            </div>

            {this.state.myWorkError && (
              <div className="myw-banner myw-banner--error">{this.state.myWorkError}</div>
            )}
            {this.state.myWorkFlash && (
              <div className="myw-banner myw-banner--ok">✓ {this.state.myWorkFlash}</div>
            )}
            {this.state.openingFileError && (
              <div className="myw-banner myw-banner--error">{this.state.openingFileError}</div>
            )}

            {/* Current block — its own card, teal border while a block is active */}
            {this.state.myActive ? (
              <div className="myw-card myw-card--active">
                <div className="myw-card__title">Current block</div>
                <div className="myw-block__row">
                  <div>
                    <div className="myw-block__id">
                      {this.state.myActive.deviceId != null ? this.state.myActive.deviceId : this.state.myActive.fileId}
                      {" "}{this.state.myActive.blockLabel}{" "}
                      {String(this.state.myActive.server || "").toUpperCase()}
                    </div>
                    <div className="myw-block__name">{this.state.myActive.name || ""}</div>
                  </div>
                  <div className="myw-block__timer">
                    {elapsedSince(this.state.myActive.assignedAt)}
                    <span className="myw-block__timerSub">time taken</span>
                  </div>
                </div>

                {this.state.myActive.warnedAt && (
                  <div className="myw-warn">
                    ⚠ Auto-returns to the queue soon if not marked completed.
                  </div>
                )}

                <div className="myw-block__actions">
                  <button
                    type="button"
                    className="vp-btn vp-btn--ghost"
                    onClick={() => this.openFile(this.state.myActive)}
                    disabled={this.state.openingFile}
                  >
                    {this.state.openingFile ? "Opening…" : "Open file"}
                  </button>
                  <button
                    type="button"
                    className="vp-btn"
                    onClick={() => this.markCompleted()}
                    disabled={this.state.myWorkBusy}
                  >
                    Mark completed
                  </button>
                  <button
                    type="button"
                    className="vp-btn vp-btn--warn"
                    onClick={this.markReanalysis}
                    disabled={this.state.myWorkBusy}
                  >
                    Reanalysis needed
                  </button>
                </div>
              </div>
            ) : (
              <div className="myw-card">
                <div className="myw-card__title">Current block</div>
                <div className="myw-block__name" style={{ marginBottom: 10 }}>
                  {this.state.myWorkLoading ? "Loading…" : "No active block."}
                </div>
                <button
                  type="button"
                  className="vp-btn"
                  onClick={this.requestBlock}
                  disabled={this.state.myWorkBusy}
                >
                  {this.state.myWorkBusy ? "Requesting…" : "Request file"}
                </button>
              </div>
            )}

            {/* Waiting for reanalysis — its own card, only when non-empty */}
            {this.state.myReanalysis.length > 0 && (
              <div className="myw-card">
                <div className="myw-card__title">Waiting for reanalysis</div>
                {this.state.myReanalysis.map((b) => (
                  <div className="myw-row" key={`${b.server}-${b.fileId}-${b.blockIndex}`}>
                    <span className="myw-row__id">
                      {b.deviceId != null ? b.deviceId : b.fileId} {b.blockLabel} {String(b.server || "").toUpperCase()}
                    </span>
                    <span className="myw-row__name">{b.name || ""}</span>
                    <span className={"myw-chip " + (b.ready ? "myw-chip--ready" : "myw-chip--running")}>
                      {b.ready ? "Ready" : "Running"}
                    </span>
                    <span className="myw-row__actions">
                      <button
                        type="button"
                        className="vp-btn vp-btn--ghost vp-btn--sm"
                        onClick={() => this.openFile(b)}
                        disabled={this.state.openingFile || !b.lastDatasetId}
                      >
                        {this.state.openingFile ? "Opening…" : "Open file"}
                      </button>
                      <button
                        type="button"
                        className="vp-btn vp-btn--sm"
                        onClick={() => this.markCompleted(b)}
                        disabled={this.state.myWorkBusy}
                      >
                        Mark completed
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Completed today — its own card */}
            <div className="myw-card">
              <div className="myw-card__title">Completed today</div>
              {this.state.myCompletedToday.length === 0 ? (
                <div style={{ opacity: 0.6, fontSize: 13, padding: "4px 0" }}>Nothing yet.</div>
              ) : (
                this.state.myCompletedToday.map((b) => (
                  <div className="myw-row" key={`${b.server}-${b.fileId}-${b.finishedAt}`}>
                    <span className="myw-row__id">
                      {b.deviceId != null ? b.deviceId : b.fileId} {b.blockLabel} {String(b.server || "").toUpperCase()}
                    </span>
                    <span className="myw-row__name">{b.name || ""}</span>
                  </div>
                ))
              )}
            </div>

            {/* ── Attendance & Verified Activity (primary) ────────── */}
            <div className="vp-card">
              <div className="vp-card__head">
                <h3>My Attendance & Verified Activity</h3>
                <span className="vp-card__hint">
                  Login / break / files & patients verified — Today, Week,
                  Month, or Custom range
                </span>
              </div>
              <AttendanceView selfOnly />
            </div>

            {/* ── Today's verified patients ───────────────────────── */}
            <div className="vp-card">
              <div className="vp-card__head">
                <h3>Today's Verified Patients</h3>
                <span className="vp-card__hint">
                  {this.state.verifiedTodayLoading
                    ? "Loading…"
                    : this.state.verifiedToday.length
                      + " patient" + (this.state.verifiedToday.length === 1 ? "" : "s")
                      + " verified today"}
                  {" · "}
                  <button
                    type="button"
                    onClick={this.loadVerifiedToday}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#60a5fa",
                      cursor: "pointer",
                      padding: 0,
                      fontSize: "inherit",
                    }}
                  >↻ refresh</button>
                </span>
              </div>

              {this.state.verifiedTodayError && (
                <div
                  style={{
                    marginBottom: 12,
                    padding: "8px 12px",
                    borderRadius: 6,
                    background: "rgba(239,68,68,0.10)",
                    border: "1px solid rgba(239,68,68,0.35)",
                    color: "#ff6b6b",
                    fontSize: 13,
                  }}
                >{this.state.verifiedTodayError}</div>
              )}

              {!this.state.verifiedTodayLoading && !this.state.verifiedToday.length && !this.state.verifiedTodayError && (
                <div style={{ padding: "16px 0", opacity: 0.7, fontSize: 14 }}>
                  No patients verified today yet.
                </div>
              )}

              {this.state.verifiedToday.length > 0 && (
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 13,
                    }}
                  >
                    <thead>
                      <tr style={{ textAlign: "left", opacity: 0.75 }}>
                        <th style={{ padding: "8px 6px" }}>#</th>
                        <th style={{ padding: "8px 6px" }}>Device ID</th>
                        <th style={{ padding: "8px 6px" }}>Patient Name</th>
                        <th style={{ padding: "8px 6px" }}>Files Verified</th>
                      </tr>
                    </thead>
                    <tbody>
                      {this.state.verifiedToday.map((r, i) => (
                        <tr
                          key={r.patientId || i}
                          style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
                        >
                          <td style={{ padding: "8px 6px", opacity: 0.6 }}>{i + 1}</td>
                          <td style={{ padding: "8px 6px", fontFamily: "monospace" }}>
                            {r.deviceId != null
                              ? String(r.deviceId).padStart(6, "0")
                              : "—"}
                          </td>
                          <td style={{ padding: "8px 6px" }}>{r.name || "—"}</td>
                          <td style={{ padding: "8px 6px" }}>{r.fileCount || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ borderTop: "1px solid rgba(255,255,255,0.12)", fontWeight: 600 }}>
                        <td style={{ padding: "8px 6px" }} colSpan={3}>
                          Total ({this.state.verifiedToday.length})
                        </td>
                        <td style={{ padding: "8px 6px" }}>
                          {this.state.verifiedToday.reduce((s, p) => s + (p.fileCount || 0), 0)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>

            {/* ── Personal details (secondary, below attendance) ──── */}
            <div className="vp-card">
              <div className="vp-card__head">
                <h3>Personal Details</h3>
                {savedAt && (
                  <span className="vp-saved">
                    Saved {savedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </span>
                )}
              </div>

              <form onSubmit={this.onSubmit} className="vp-form" autoComplete="off">
                <div className="vp-grid">
                  <label className="vp-field">
                    <span className="vp-field__label">Name</span>
                    <input
                      type="text"
                      name="name"
                      required
                      onChange={this.onChange}
                      value={name}
                      placeholder="Full name"
                      className={
                        "vp-input" + (errors.name ? " vp-input--error" : "")
                      }
                    />
                  </label>

                  <label className="vp-field">
                    <span className="vp-field__label">Email</span>
                    <input
                      type="email"
                      name="email"
                      required
                      onChange={this.onChange}
                      value={email}
                      placeholder="email@example.com"
                      className={
                        "vp-input" + (errors.email ? " vp-input--error" : "")
                      }
                    />
                  </label>

                  <label className="vp-field">
                    <span className="vp-field__label">Mobile</span>
                    <input
                      type="tel"
                      name="phone"
                      required
                      onChange={this.onChange}
                      value={phone}
                      placeholder="Phone"
                      className={
                        "vp-input" + (errors.phone ? " vp-input--error" : "")
                      }
                    />
                  </label>

                  <label className="vp-field">
                    <span className="vp-field__label">Gender</span>
                    <div className="vp-radio-row">
                      <label className="vp-radio">
                        <input
                          type="radio"
                          name="gender"
                          value="M"
                          checked={gender === "M"}
                          onChange={this.onChange}
                        />
                        <span>Male</span>
                      </label>
                      <label className="vp-radio">
                        <input
                          type="radio"
                          name="gender"
                          value="F"
                          checked={gender === "F"}
                          onChange={this.onChange}
                        />
                        <span>Female</span>
                      </label>
                    </div>
                  </label>

                  <label className="vp-field vp-field--wide">
                    <span className="vp-field__label">Address</span>
                    <textarea
                      name="address"
                      onChange={this.onChange}
                      value={address}
                      rows={3}
                      placeholder="Address"
                      className="vp-input vp-input--multiline"
                    />
                  </label>
                </div>

                <div className="vp-actions">
                  <button
                    type="submit"
                    className="vp-btn"
                    disabled={saving}
                  >
                    {saving ? "Saving…" : "Update"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

VerifierProfile.propTypes = {
  updateProfile: PropTypes.func.isRequired,
  auth: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  errors: state.errors,
});

export default connect(mapStateToProps, { updateProfile })(
  withRouter(VerifierProfile)
);
